// import uniqueId from 'lodash/uniqueId';
import includes from 'lodash/includes';
import compact from 'lodash/compact';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import orderBy from 'lodash/orderBy';
import { noop } from 'lodash';
import { DATE_FORMAT } from '../../constants';

// import { saveUserPreferences } from '../../api/workspace/service';

export const formatRowActionDefs = (initialRowActionDefs) => {
  if (!initialRowActionDefs) return {};
  const actionsObj = {};
  initialRowActionDefs.forEach((row) => {
    actionsObj[row.label] = { ...row };
  });
  return actionsObj;
};

const exclude = ['selector', 'possibleActions', 'transferId'];

export const cellDisplayFormatter =
  (locale) =>
  ({ column, rowData }) => {
    // if field is undefined and is not a number type then return the
    // grid empty ndash character
    if (!rowData[column.field]) {
      return locale['common.grid.empty'];
    }

    switch (column.type) {
      case 'date':
        if (!rowData[column.field]) return locale['common.grid.empty'];
        return moment(rowData[column.field]).format(DATE_FORMAT);
      default:
        return rowData[column.field];
    }
  };

export const formatData = (data, rowActionDefs) => {
  const allowedActions = Object.keys(rowActionDefs);
  return data.map((row) => {
    const clonedRow = cloneDeep(row);
    // if the row has a possibleActions attriute
    if (clonedRow.possibleActions) {
      const possibleActions =
        clonedRow && clonedRow.possibleActions ? clonedRow.possibleActions : clonedRow.actions;
      // remove actions from column that are not in the clonedRowActions
      delete clonedRow.possibleActions;
      clonedRow.actions = compact(
        possibleActions.map((action) => (includes(allowedActions, action) ? action : null))
      );
    } else {
      // if the row does NOT have a possibleActions attribute
      clonedRow.actions = clonedRow.actions
        ? compact(
            clonedRow.actions.map((action) => (includes(allowedActions, action) ? action : null))
          )
        : [];
    }
    // const id = uniqueId();
    // return { ...clonedRow, id };
    return { ...clonedRow };
  });
};

const sortByNumber = ({ column, direction, field, rows }) => {
  // Use filterValueGetter if it exists on columnDefs
  const getValue = column.filterValueGetter ? column.filterValueGetter : ({ data }) => data[field];
  const sortedData =
    direction === 'asc'
      ? [...rows].sort((a, b) => Number(getValue({ data: a })) - Number(getValue({ data: b })))
      : [...rows].sort((a, b) => Number(getValue({ data: b })) - Number(getValue({ data: a })));
  return sortedData;
};

const sortByDate = ({ column, direction, field, rows }) => {
  const getValue = column.filterValueGetter ? column.filterValueGetter : ({ data }) => data[field];
  const sortedData =
    direction === 'asc'
      ? [...rows].sort((a, b) => new Date(getValue({ data: a })) - new Date(getValue({ data: b })))
      : [...rows].sort(
          (a, b) => new Date(getValue({ data: b })) - new Date(getValue({ data: a }))
        );
  return sortedData;
};

// TODO: Maybe this function could just use columnState instead
export const sortData = ({ columnDefs, rows, sortState }) => {
  const { direction, field } = sortState || {};
  const column = columnDefs.find((col) => col.field === field) || {};
  const type = column?.sortAs || column?.type;
  const numberTypesArray = ['number', 'numeric', 'currency'];
  if (numberTypesArray.includes(type)) return sortByNumber({ column, rows, direction, field });
  if (type === 'date') return sortByDate({ column, rows, direction, field });
  const getValue = column.filterValueGetter ? column.filterValueGetter : ({ data }) => data[field];
  return orderBy(rows, [(item) => getValue({ data: item })?.toLowerCase()], [direction]);
};

export const NativeColumn =
  ({ exclude = [], override = [], primary = [] }) =>
  (col) => {
    // // extract the things, we need
    const { cellDisplay, code, field, headerName, sortable, variant } = col;
    const transform = override.find((t) => t.field === col.field);

    // // is it primary?
    const isPrimary = primary.includes(col.field);

    if (exclude.includes(col.field)) {
      return false;
    }
    return {
      colId: field,
      field,
      fieldId: field,
      title: headerName,
      label: headerName,
      headerName,
      type: col.type || 'string',
      primary: isPrimary || col.primary,
      rowIdColumn: field,
      variant,
      code,
      hide: !!col.hide,
      sortable,
      ...(transform ? transform.defs : {}),
      cellDisplay: cellDisplay || !noop
    };
  };

/**
 * Transform the columns from basicGrid to basicMobileGrid format.
 */
export const transformColumns = ({ columns, data, override = [], primary = [], exclude = [] }) =>
  columns
    .map(
      NativeColumn({
        exclude,
        data,
        override,
        primary
      })
    )
    .filter(Boolean);

/**
 * Build a column state, based on field names
 * It is used only, when columnState is not passed in.
 */
export const buildColumnState = (fields) =>
  fields.map((field) => ({
    fieldId: field,
    primary: true
  }));

export const sortAndExcludeColumns = (columnDefs, exclude = [], primaryDefs = []) => {
  const primary = [];
  const rest = [];

  columnDefs.forEach((x) => {
    if (!exclude.includes(x.field)) {
      if (x.primary && primary) {
        primary.splice(primaryDefs.indexOf(x.field), 0, x);
      } else {
        rest.push(x);
      }
    }
  });

  return [].concat.apply([], [primary, rest]);
};

export const actionColumns = ['selector', 'possibleActions', 'transferId'];

export const formatColumnDefs = (columnDefs, locale) =>
  columnDefs.map((column) => {
    if (actionColumns.includes(column.colId)) {
      // useMobileGrid will filter out column defs that have the field of 'actions' so they don't show in collapsable area
      return {
        ...column,
        field: 'actions',
        sortable: false,
        cellDisplay: column.cellDisplay || cellDisplayFormatter(locale)
      };
    }

    return {
      ...column,
      // If column does not specifically set sortable to false and the column is not set to hidden
      sortable: column.sortable !== false && !column.hide,
      cellDisplay: column.cellDisplay || cellDisplayFormatter(locale)
    };
  });
